import { getEnrichmentStatusSuperuserEnrichmentStatusGetOptions } from '@/client/@tanstack/react-query.gen'
import type { StatusBreakdown } from '@/client/types.gen'
import { SkeletonPanels } from '@/components/skeleton-common'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_authed/admin/enrichment-status')({
  component: AdminEnrichmentStatusPage,
  pendingComponent: () => <SkeletonPanels panels={2} rows={6} columns={4} />,
  errorComponent: () => <p className="text-destructive">Failed to load enrichment status.</p>,
  beforeLoad: () => ({
    getTitle: () => 'Enrichment Status',
  }),
})

function AdminEnrichmentStatusPage() {
  const { data } = useQuery(
    getEnrichmentStatusSuperuserEnrichmentStatusGetOptions(),
  )

  if (!data) {
    return <></>
  }

  const completedCount = getStatusCount(data.statusSummary, 'COMPLETED')
  const runningCount = getStatusCount(data.statusSummary, 'RUNNING')
  const failedCount = getStatusCount(data.statusSummary, 'FAILED')
  const insufficientDataCount = getStatusCount(data.statusSummary, 'COMPLETED_INSUFFICIENT_DATA')
  const notStartedCount = getNotStartedCount(data.statusSummary)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Enrichment Status</h1>
        <p className="text-muted-foreground">
          Per-organization account processing status breakdown.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          title="Total Accounts"
          value={formatNumber(data.totalAccounts)}
          subtitle="All accounts across orgs"
        />
        <StatCard
          title="Completed"
          value={formatNumber(completedCount)}
          subtitle="Successfully enriched"
        />
        <StatCard
          title="Running"
          value={formatNumber(runningCount)}
          subtitle="Currently processing"
        />
        <StatCard
          title="Failed"
          value={formatNumber(failedCount)}
          subtitle="Enrichment failed"
        />
        <StatCard
          title="Insufficient Data"
          value={formatNumber(insufficientDataCount)}
          subtitle="No usable data found"
        />
        <StatCard
          title="Not Started"
          value={formatNumber(notStartedCount)}
          subtitle="Awaiting enrichment"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Per-Organization Breakdown</CardTitle>
          <CardDescription>
            Account enrichment status for each organization. Generated at
            {' '}
            {formatTimestamp(data.generatedAt)}
            .
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Org Name</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Running</TableHead>
                <TableHead>Failed</TableHead>
                <TableHead>Insufficient Data</TableHead>
                <TableHead>Not Started</TableHead>
                <TableHead>Oldest Account</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.organizations.map(org => (
                <TableRow key={org.orgId}>
                  <TableCell className="font-medium">{org.orgName}</TableCell>
                  <TableCell>{formatNumber(org.totalAccounts)}</TableCell>
                  <TableCell>
                    <StatusBadge status="COMPLETED" count={getStatusCount(org.statuses, 'COMPLETED')} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status="RUNNING" count={getStatusCount(org.statuses, 'RUNNING')} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status="FAILED" count={getStatusCount(org.statuses, 'FAILED')} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status="COMPLETED_INSUFFICIENT_DATA" count={getStatusCount(org.statuses, 'COMPLETED_INSUFFICIENT_DATA')} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge count={getNotStartedCount(org.statuses)} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatTimestamp(getOldestDate(org.statuses))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

function StatCard({ title, value, subtitle }: { title: string, value: string, subtitle: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">
        {subtitle}
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status, count }: { status?: string, count: number }) {
  if (count === 0) {
    return <span className="text-muted-foreground">0</span>
  }

  return (
    <Badge variant={getStatusVariant(status)}>
      {formatNumber(count)}
    </Badge>
  )
}

function getStatusVariant(status?: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'COMPLETED') {
    return 'default'
  }
  if (status === 'RUNNING') {
    return 'secondary'
  }
  if (status === 'FAILED') {
    return 'destructive'
  }
  if (status === 'COMPLETED_INSUFFICIENT_DATA') {
    return 'secondary'
  }
  return 'outline'
}

function getStatusCount(statuses: Array<StatusBreakdown>, status: string): number {
  const entry = statuses.find(s => s.status === status)
  return entry?.count ?? 0
}

function getNotStartedCount(statuses: Array<StatusBreakdown>): number {
  const entry = statuses.find(s => !s.status)
  return entry?.count ?? 0
}

function getOldestDate(statuses: Array<StatusBreakdown>): string | undefined {
  let oldest: string | undefined
  for (const s of statuses) {
    if (!s.oldestAccountAt) {
      continue
    }
    if (!oldest || s.oldestAccountAt < oldest) {
      oldest = s.oldestAccountAt
    }
  }
  return oldest
}

function formatTimestamp(value: string | undefined): string {
  if (!value) {
    return 'n/a'
  }
  return new Date(value).toLocaleString()
}

function formatNumber(value: number): string {
  return value.toLocaleString()
}
